import React, { useState } from 'react';
import { styled, Grid, Box, Typography } from '@mui/material';
import { FormControlLabel, Checkbox } from '@mui/material';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useTextInput, useNumberInput, useToggle, useDatePicker } from 'hooks/forms';
import { Section, Header, Content } from 'components/ui/forms';
import { SubmitButton } from 'components/ui/buttons';
import { CancelButton } from 'components/ui/buttons';
import { TextField, ProvinceSelector, DatePicker } from 'components/ui/inputs';
import { useNotification } from 'context/notification';
import {
    createRepresentative,
    updateRepresentative,
} from 'pages/stripeOnboarding/StripeOnboardingAPI';
import { ActionButton } from 'components/ui/buttons';

export default function Representative(props) {
    const { hostId, connectId, business, representative, readOnly, onUpdate } = props;

    const { businessType, structure } = business;

    const [loading, setLoading] = useState(false);

    const [modalOpen, setModalOpen] = useState(false);

    const { notify } = useNotification();

    const handleModalOpen = () => setModalOpen(true);
    const handleModalClose = () => setModalOpen(false);

    const handleRepresentativeCreate = (values) => {
        setLoading(true);

        createRepresentative(hostId, connectId, values)
            .then((persons) => {
                notify.success('Created Representative');
                onUpdate(persons);
            })
            .catch((error) => notify.error(error.message))
            .finally(() => setLoading(false));
    };

    const handleRepresentativeUpdate = (values) => {
        setLoading(true);

        updateRepresentative(hostId, connectId, representative.id, values)
            .then((persons) => {
                notify.success('Updated Representative');
                onUpdate(persons);
            })
            .catch((error) => notify.error(error.message))
            .finally(() => {
                setLoading(false);
                setModalOpen(false);
            });
    };

    return (
        <Section>
            <Header>Account Representative</Header>
            <Typography>
                This is the person who manages your organization's account details. In most cases,
                this is you, the person completing this form. They will have access to your business
                information and can update it as required. Make sure the details entered match their
                government-issued photo ID (for example, a driver's license). If there's a
                discrepancy, you may be asked to upload a copy of the ID to confirm.
            </Typography>
            <Content>
                {representative ? (
                    <RepresentativeCard representative={representative} onEdit={handleModalOpen} />
                ) : (
                    <RepresentativeForm
                        editMode={false}
                        readOnly={readOnly}
                        representative={null}
                        businessType={businessType}
                        structure={structure}
                        onSubmit={handleRepresentativeCreate}
                        loading={loading}
                    />
                )}
            </Content>

            {modalOpen && (
                <Dialog open onClose={handleModalClose}>
                    <DialogTitle>Edit Representative</DialogTitle>
                    <DialogContent>
                        <RepresentativeForm
                            editMode={true}
                            readOnly={readOnly}
                            representative={representative}
                            businessType={businessType}
                            structure={structure}
                            onSubmit={handleRepresentativeUpdate}
                            onClose={handleModalClose}
                            loading={loading}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </Section>
    );
}

function RepresentativeCard(props) {
    const { representative, onEdit } = props;

    return (
        <Box
            border={'1px solid #888'}
            borderRadius="8px"
            p={4}
            pl={2}
            pr={2}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'space-between'}
        >
            <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                <RepresentativeIcon />
                <Box ml={2}>
                    <RepresentativeName>
                        {representative.firstName} {representative.lastName}
                    </RepresentativeName>
                    <RepresentativeEmail>{representative.email}</RepresentativeEmail>
                </Box>
            </Box>
            <Box>
                <ActionButton onClick={onEdit}>Edit</ActionButton>
            </Box>
        </Box>
    );
}

function RepresentativeForm(props) {
    const {
        editMode,
        representative,
        businessType,
        structure,
        readOnly,
        onSubmit,
        onClose,
        loading,
    } = props;

    const { notify } = useNotification();

    const firstName = useTextInput({ disabled: readOnly, defaultValue: representative?.firstName });
    const lastName = useTextInput({ disabled: readOnly, defaultValue: representative?.lastName });
    const email = useTextInput({ disabled: readOnly, defaultValue: representative?.email });
    const phone = useNumberInput({
        disabled: readOnly,
        defaultValue: representative?.phone,
        integerOnly: true,
    });
    const title = useTextInput({ disabled: readOnly, defaultValue: representative?.title });
    const dob = useDatePicker({ disabled: readOnly, defaultValue: representative?.dob });
    const city = useTextInput({ disabled: readOnly, defaultValue: representative?.city });
    const address = useTextInput({ disabled: readOnly, defaultValue: representative?.address });
    const unit = useTextInput({ disabled: readOnly, defaultValue: representative?.unit });
    const postal = useTextInput({ disabled: readOnly, defaultValue: representative?.postal });
    const province = useTextInput({ disabled: readOnly, defaultValue: representative?.province });
    const director = useToggle({ disabled: readOnly, defaultValue: representative?.director });
    const owner = useToggle({ disabled: readOnly, defaultValue: representative?.owner });

    const handleSubmit = () => {
        let formError = false;

        // *** Check for Empty Fields *** //

        [firstName, lastName, email, phone, title, city, address, postal, province].forEach(
            (field) => {
                if (field.value.trim().length === 0) {
                    field.setError('This field cannot be empty');
                    formError = true;
                }
            },
        );

        // *** Check for Valid DoB *** //

        if (dob.value === null || dob.value.isValid() === false) {
            formError = true;
        }

        // *** Form Invalid *** //

        if (formError) {
            notify.error('Please fill out all fields');
            return;
        }

        // *** Submit Form *** //

        const formValues = {
            firstName: firstName.value.trim(),
            lastName: lastName.value.trim(),
            email: email.value.trim(),
            phone: phone.value,
            title: title.value.trim(),
            dob: dob.value.format('YYYY-MM-DD'),
            city: city.value.trim(),
            address: address.value.trim(),
            unit: unit.value.trim(),
            postal: postal.value.trim(),
            province: province.value.trim(),
            director: director.checked,
        };

        if (businessType === 'company' && structure === 'private_corporation') {
            formValues.owner = owner.checked;
        }

        onSubmit(formValues);
    };

    return (
        <Box>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TextField label="First Name" {...firstName.formProps} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField label="Last Name" {...lastName.formProps} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField label="Email Address" {...email.formProps} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField label="Phone Number" {...phone.formProps} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DatePicker label="Date of Birth" {...dob.formProps} />
                </Grid>
                <Grid item xs={12}>
                    <TextField label="Job Title with this Business" {...title.formProps} />
                </Grid>
                <Grid item xs={9} md={8}>
                    <TextField label="Home Address" {...address.formProps} />
                </Grid>
                <Grid item xs={3} md={4}>
                    <TextField label="Unit #" {...unit.formProps} />
                </Grid>
                <Grid item xs={6} sm={6} md={4}>
                    <TextField label="City" {...city.formProps} />
                </Grid>
                <Grid item xs={6} sm={6} md={4}>
                    <ProvinceSelector region="CA" {...province.formProps} />
                </Grid>
                <Grid item xs={6} sm={6} md={4}>
                    <TextField label="Postal Code" {...postal.formProps} />
                </Grid>
                <Grid item xs={12}>
                    <Box display={'flex'} flexDirection={'column'} alignItems={'flex-start'}>
                        {businessType === 'non_profit' && (
                            <FormControlLabel
                                control={<Checkbox {...director.formProps} />}
                                label={'I am a member of the Board of Directors'}
                            />
                        )}
                        {businessType === 'company' && structure === 'private_corporation' && (
                            <>
                                <FormControlLabel
                                    control={<Checkbox {...owner.formProps} />}
                                    label={'I own 25% or more of the company'}
                                />
                                <FormControlLabel
                                    control={<Checkbox {...director.formProps} />}
                                    label={
                                        "I'm a director of the company, or a member of the governing board"
                                    }
                                />
                            </>
                        )}
                    </Box>
                </Grid>
            </Grid>
            <Box display="flex" justifyContent="flex-end" mt={4}>
                {editMode && (
                    <CancelButton onClick={onClose} disabled={loading}>
                        Cancel
                    </CancelButton>
                )}
                <SubmitButton
                    onClick={handleSubmit}
                    loading={loading}
                    disabled={loading || readOnly}
                >
                    Save
                </SubmitButton>
            </Box>
        </Box>
    );
}

const RepresentativeIcon = styled(AccountCircleIcon)(({ theme }) => ({
    fontSize: 52,
}));

const RepresentativeName = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
}));

const RepresentativeEmail = styled(Typography)(({ theme }) => ({}));
